import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import {
	CarlineSelect,
	DetalleMotivo,
	Loading,
	MultipleCTASelection,
	NombreApellidos,
} from 'src/components';
import { RadioTextGroup } from 'src/components/inputs/RadioTextGroup';
import {
	FormController,
	LayoutTemplate,
	useOneFormContext,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { ConfirmacionLEM, ThanKYouLEM } from 'src/forms/LEM';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { formDataBuilder } from '../format-fields';
import {
	addRemovedStep,
	getIndexInSteps,
	removeStep,
	resetStepsScreenIndex,
} from '../helpers/add-remove-steps';
import { LEMFormProps } from './LEMController';
import {
	checkIdLeadHasCarline,
	useInitializeForm,
} from './use-initialize-lem-form';

const getPeriodoContactoTitle = (modeloPrecargado: boolean) => {
	if (modeloPrecargado) {
		return (
			<>
				<Text bold>Y ¿cuándo</Text> te{' '}
				<span style={{ whiteSpace: 'nowrap' }}>
					respondieron?
					<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
				</span>
			</>
		);
	} else {
		return (
			<>
				Nos encantaría saber más sobre tu experiencia.{' '}
				<Text bold>
					¿Cuándo te{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						respondieron?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</Text>
			</>
		);
	}
};

export function LEMContactadoForm(props: LEMFormProps) {
	const {
		dpsData,
		idLead,
		idPerson,
		installation,
		dealersInfo,
		handleError,
		presubmit,
		trackFormLoad,
	} = props;
	const [presubmitSent, setPresubmitSent] = useState(false);
	const { initializeForm, handleNextStep } = useOneFormContext();
	const store = useStore();
	const { enviarConcesion } = useSelector(
		(state: OneFormState) => state.formData.fields,
	);
	const dispatch = useDispatch();

	const ctasPeriodoContacto = [
		{
			label: 'En las primeras 8 horas',
			value: 'En las primeras 8h',
		},
		{
			label: 'En las primeras 24 horas',
			value: 'En las primeras 24h',
		},
		{
			label: 'En más de 24 horas',
			value: 'En más de 24h',
		},
	];

	const ctasInformacionUtil = [
		{
			label: 'Sí, resolvieron mis dudas y/o me dieron una cita',
			value: 'Sí, resolvieron mis dudas y/o me dieron una cita',
		},
		{
			label: 'No, necesito más información',
			value: 'No, necesito más información',
		},
		{
			label: 'Aún la estoy valorando',
			value: 'Aún la estoy valorando',
		},
	];

	const optionsEnviarConcesion = [
		{
			label: 'Sí, mis respuestas pueden ser enviadas y analizadas junto a mi nombre',
			info: 'El consentimiento puede revocarse en cualquier momento a través de los canales de contacto que se indican a continuación*.',
			value: '1',
		},
		{
			label: 'Mis respuestas pueden ser enviadas pero sin mencionar mi nombre',
			info: 'Seleccionando esta opción, el punto de venta no podrá ponerse en contacto contigo en relación a tus comentarios.',
			value: '2',
		},
		{
			label: 'Mis respuestas solo deben ser analizadas de manera anónima',
			info: 'Seleccionando esta opción, tus respuestas serán analizadas de modo agregado junto al resto de respuestas de todos los entrevistados y no se trasladarán a los comentarios abiertos.',
			value: '0',
		},
	];

	let index = 0;
	const stepsWithModel: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Por qué modelo </Text>contactaste con tu punto
					de venta?
				</>
			),
			fields: (
				<LayoutTemplate element={<CarlineSelect useCarline={true} />} />
			),
			screenIndex: index++,
			name: 'Modelo',
			outputs: ['modelo'],
			outputsText: ['modelo'],
		},
		{
			title: getPeriodoContactoTitle(checkIdLeadHasCarline(idLead)),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={ctasPeriodoContacto}
							name="periodoContacto"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'PeriodoContacto',
			outputs: ['periodoContacto'],
			outputsText: ['periodoContacto'],
			ctas: ctasPeriodoContacto,
		},
		{
			title: (
				<>
					<Text bold>¿Fue útil la información</Text> que te dieron?
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={ctasInformacionUtil}
							name="informacionUtil"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'InformacionUtil',
			outputs: ['informacionUtil'],
			outputsText: ['InformacionUtil'],
			ctas: ctasInformacionUtil,
		},
		{
			title: (
				<>
					Finalmente nos gustaría saber si podemos{' '}
					<Text bold>
						enviar tus respuestas junto con tu nombre al punto de
						venta
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<RadioTextGroup
							name="enviarConcesion"
							options={optionsEnviarConcesion}
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'EnviarConcesion',
			outputs: ['enviarConcesion'],
			outputsText: ['enviarConcesion'],
			ctas: optionsEnviarConcesion,
		},
		{
			title: (
				<>
					Por favor,{' '}
					<Text bold>introduce o comprueba tus datos:</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div style={{ paddingBottom: '2px' }}>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'DatosPersonales',
			outputs: ['datosPersonales'],
			outputsText: ['datosPersonales'],
		},
		{
			title: (
				<>
					<Text bold>¿Quieres añadir </Text> algún comentario
					adicional?
				</>
			),
			fields: (
				<LayoutTemplate element={<DetalleMotivo name="comentario" />} />
			),
			screenIndex: index++,
			name: 'Comentario',
			outputs: ['comentario'],
			outputsText: ['comentario'],
		},
		{
			fields: <ConfirmacionLEM />,
			screenIndex: index++,
			name: 'Confirmacion',
		},
	];

	const LEMContactadoData: Partial<FormDataGroup> = {
		headers: {},
		fields: {
			formName: 'lem-contactado',
			formType: 'otherform',
			pageCategory: 'Encuesta',
		}, // Analítica
	};

	useEffect(() => {
		if (enviarConcesion) {
			modifySteps(
				enviarConcesion == '1' || enviarConcesion == 'true',
				'DatosPersonales',
			);
		}
	}, [enviarConcesion]);

	const modifySteps = (add: boolean, screen: string) => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];
		if (add) {
			const stepIndex = getIndexInSteps(newSteps, screen);

			if (stepIndex === -1) {
				newSteps = addRemovedStep(
					steps,
					actualSteps,
					screen,
					screenIndex,
				);
				setTimeout(() => {
					handleNextStep();
				}, 0);
			}
		} else {
			newSteps = removeStep(newSteps, screen);
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
	};

	const generateData = (data?: Partial<FormDataGroup>) => {
		const { formData, dealersInfo }: OneFormState = store.getState();
		const { fields } = formData;
		const { tc, today, url, device } = getParamsForm();

		return {
			'headers[DPS_TOKEN]': 'VW_DDB_FRM_LEM_ENCUESTA',
			'headers[FK_SYSTEM]': idLead.Id,
			'headers[FORM_DATE_INSERTION]': today,
			'headers[FORM_DETAILS]': 'LEM Contactado',
			'headers[FORM_NAME]': fields.formName
				? `vw-oneform-${fields.formName}`
				: `vw-oneform-${data.fields.formName}`,
			'headers[FORM_OBJECTIVE]': 'Encuesta',
			'headers[FORM_URL]': url,
			'headers[FORM_VERSION]': fields.formName
				? `vw-oneform-${fields.formName}-${process.env.VERSION}`
				: `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
			'headers[ORIGIN_DEVICE_TYPE]': device,
			'headers[ORIGIN_TRACKING_CODE]': tc,
			'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,

			'flags[PERSON_RGPD_ADAPTED]': true,
			'flags[FLAG_RELACION_MARCA_SIN_IC]': false,

			'fields[DPS_BRAND]': 'Volkswagen',
			'fields[PERSON_EMAIL]': idLead.Email,
			'fields[PERSON_NAME]': fields.name ? fields.name : idLead.FirstName,
			'fields[PERSON_PHONE_MOBILE]': idLead.MobilePhone,
			'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi
				? fields.lssi
				: 'NO INFORMADO',
			'fields[PERSON_RGPD_PERFILADO]': fields.tmk
				? fields.tmk
				: 'NO INFORMADO',
			'fields[PERSON_SURNAME_1]': fields.surname
				? fields.surname
				: idLead.LastName,
			'fields[PERSON_SURNAME_2]': fields.secondSurname
				? fields.secondSurname
				: idLead.MiddleName,
			'fields[SF_LEAD_INSTALLATION_ID]': idLead.Dealership__c,
			'fields[SF_LEAD_INSTALLATION_CODE]': installation
				? installation.VGED_DealerCode__c
				: '',
			'fields[SF_LEAD_INSTALLATION_NAME]': installation
				? installation.VGED_CommercialName__c
				: '',
			'fields[SURVEY_ANSWER_01]': dpsData.rating,
			'fields[SURVEY_ANSWER_03]': fields.periodoContacto,
			'fields[SURVEY_ANSWER_18]': fields.informacionUtil,
			'fields[SURVEY_ANSWER_10]': fields.enviarConcesion,
			'fields[SURVEY_ANSWER_11]': fields.comentario,
			'fields[VEH_INTEREST_NEW_OR_USED]': 'New Car',
			'fields[VEH_INTEREST_ID]':
				checkIdLeadHasCarline(idLead) && dealersInfo?.selectedCarline
					? dealersInfo.selectedCarline.code
					: fields.carlineCode,
			'fields[VEH_INTEREST_MODEL]':
				checkIdLeadHasCarline(idLead) && dealersInfo?.selectedCarline
					? dealersInfo.selectedCarline.title
					: fields.modelo,
		};
	};

	const generatePresubmitData = () => {
		const data = generateData(LEMContactadoData);

		data['headers[FORM_DETAILS]'] = 'LEM Pregrabación';
		data['headers[DPS_TOKEN]'] = 'VW_DDB_FRM_LEM_ENCUESTA_PREGRABACION';

		return data;
	};

	useEffect(() => {
		if (!presubmitSent && presubmit) sendForm(true);
	}, []);

	const sendForm = (isPresubmit?: boolean): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = isPresubmit ? generatePresubmitData() : generateData();
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				if (isPresubmit) setPresubmitSent(true);
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				if (isPresubmit) handleError();
				resolve(false);
			}
		});
	};

	const { loading, steps, firstStep, lastStep } = useInitializeForm(
		stepsWithModel,
		idLead,
	);

	useEffect(() => {
		const LEMContactadoInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens: steps.length,
			screenIndex: firstStep.screenIndex,
			formTheme: 'main',
			notALayer: true,
			formType: 'inPage',
			step: firstStep,
			steps,
			stepsHistory: [0],
			thankYouComponent: <ThanKYouLEM />,
		};
		if (!loading) {
			initializeForm(LEMContactadoInfo, LEMContactadoData, true);
			trackFormLoad();
		}
	}, [loading]);

	return presubmitSent && steps ? (
		<FormController steps={steps} screenType="full-screen" />
	) : (
		<Loading />
	);
}
